import { MouseEvent, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import Button, { ButtonState } from 'src/components/design-system/Button';
import PopUp from 'src/components/design-system/PopUp';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import useCompany from 'src/hooks/useCompany';
import useConfirm from 'src/hooks/useConfirm';
import useInvoiceScanManager from 'src/hooks/useInvoiceScanManager';
import useRedirect from 'src/hooks/useRedirect';
import { MergeAttachmentStatus } from 'src/services/useReviewDocumentApi';

export default function MergeButton() {
  const { company } = useCompany();
  const { confirm, onCancel } = useConfirm();
  const { getUrl, redirect } = useRedirect();
  const {
    ocrPreviewDocument,
    closeReviewModal,
    refreshPage,
    initialReviewDocValidation,
    reviewDocumentValidations,
    docNumber,
    isReviewPanelLoading,
    mergeAttachment,
    isMergeAttachmentLoading,
    isCreateAndConfirmDocumentLoading,
  } = useInvoiceScanManager();

  const { buttonState, message, invoiceNumber, receiptId, poId } = useMemo(() => {
    const {
      receiptId = 0,
      poId = 0,
      mergeAttachmentStatus = 0,
      invoiceNumber = '',
    } = reviewDocumentValidations?.mergeAttachmentReview || {};

    const docStatus = reviewDocumentValidations?.matchStatus;
    let buttonState: ButtonState = 2;
    let message = '';
    const isCreditMemo = initialReviewDocValidation.documentType === 'Credit Memo';
    const canUseNegativeQty = initialReviewDocValidation.creditMemoPermissions?.canUseNegativeQty;

    if (isCreditMemo && !canUseNegativeQty) {
      buttonState = 2;
    } else if (docNumber.value && docStatus === 'NonOcrGeneratedCurrentCompanyExactMatch') {
      if (
        MergeAttachmentStatus[mergeAttachmentStatus] ===
        'poOpenInvoiceNotCompletedOrInvoiceCompletedAndNotProcessed'
      ) {
        buttonState = 0;
      } else if (
        MergeAttachmentStatus[mergeAttachmentStatus] === 'poClosedInvoiceCompletedAndProcessed' ||
        MergeAttachmentStatus[mergeAttachmentStatus] === 'poOpenInvoiceCompletedAndProcessed'
      ) {
        buttonState = 1;
        message = 'Cannot merge with PO/Doc in invalid state.';
      } else if (MergeAttachmentStatus[mergeAttachmentStatus] === 'userDoesNotHaveAccess') {
        buttonState = 1;
        message = `You do not have access to the selected Document: ${docNumber.value}`;
      }
    }

    return {
      receiptId: receiptId,
      poId: poId,
      invoiceNumber,
      buttonState,
      message,
    };
  }, [
    docNumber.value,
    initialReviewDocValidation.creditMemoPermissions?.canUseNegativeQty,
    initialReviewDocValidation.documentType,
    reviewDocumentValidations?.matchStatus,
    reviewDocumentValidations?.mergeAttachmentReview,
  ]);

  const handleClickRedirect = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      onCancel();
      e.stopPropagation();
      const url = getUrl('invoiceDetails')(receiptId.toString(), poId.toString());
      redirect(url, false, true);
    },
    [onCancel, getUrl, receiptId, poId, redirect]
  );

  const handleMerge = useCallback(async () => {
    const isMergeAccepted = await confirm({
      title: 'Merge Record(s)',
      content: (
        <div>
          Please confirm if you would like to proceed with merging and attaching this image file to
          the existing document{' '}
          <span onClick={handleClickRedirect} className="text-link cursor-pointer underline">
            {invoiceNumber}
          </span>
        </div>
      ),
      confirmButtonText: 'Confirm',
      cancelButtonText: 'Cancel',
      width: '500px',
    });
    if (isMergeAccepted) {
      mergeAttachment({
        attachmentId: ocrPreviewDocument?.attachId as number,
        companyId: Number(company.companyId),
        invoiceId: receiptId,
      })
        .then(() => {
          closeReviewModal();
          toast.success('Merge Attachment Successfully!');
          refreshPage();
        })
        .catch(() => {
          toast.error('Merge Attachment Unsuccessful!');
        });
    }
  }, [
    confirm,
    handleClickRedirect,
    invoiceNumber,
    mergeAttachment,
    ocrPreviewDocument?.attachId,
    company.companyId,
    receiptId,
    closeReviewModal,
    refreshPage,
  ]);

  return (
    <>
      <PopUp
        content={
          <div className="m-1 max-w-[300px]">
            <PopUpContentWrapper type="Error" contents={[message]} />
          </div>
        }
        show={buttonState === 1}
      >
        <Button
          color="info"
          onClick={handleMerge}
          buttonState={buttonState}
          disabled={
            isMergeAttachmentLoading || isReviewPanelLoading || isCreateAndConfirmDocumentLoading
          }
        >
          Merge
        </Button>
      </PopUp>
    </>
  );
}
